import { useRouter } from 'next/router';

import React, { IframeHTMLAttributes, useCallback, useEffect, useRef } from 'react';

import { postIframeMessageState } from 'atoms/iframe';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import styled from 'styled-components';

import { GetMessageByIframeType, SendMessageToIframeType } from '@customTypes/Iframe';

interface IframeContainerProps extends IframeHTMLAttributes<HTMLIFrameElement> {
  pageUrl: string;
  onLoadData?: SendMessageToIframeType;
}

const Iframe = ({ pageUrl, onLoadData, ...rest }: IframeContainerProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const router = useRouter();

  const targetOrigin = new URL(pageUrl).origin;

  const onMessage = useCallback(
    (message: GetMessageByIframeType) => {
      switch (message.type) {
        case 'navigation-route':
          const navigationRoute = (destination: string) => {
            const { origin, pathname, search } = new URL(destination);
            if (origin === window.location.origin) {
              router.push(pathname + search);
            } else {
              window.location.href = destination;
            }
          };

          const { destination } = message.data;
          navigationRoute(destination);
          break;
        case 'scroll-to-iframe':
          iframeRef.current?.scrollIntoView({ behavior: 'auto' });
        default:
          break;
      }
    },
    [router]
  );

  const messageHandler = useCallback(
    (event: MessageEvent) => {
      const trustEvent = event.origin === targetOrigin && typeof event.data === 'string';

      if (trustEvent) {
        try {
          const message = JSON.parse(event.data);
          onMessage(message);
        } catch (error) {
          console.error('Failed to parse iframe event data', error);
        }
      }
    },
    [onMessage, targetOrigin]
  );

  const handleOnLoad = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(JSON.stringify(onLoadData), targetOrigin);
  }, [onLoadData, targetOrigin]);
  const postIframeMessage = useRecoilValue(postIframeMessageState);
  const resetPostIframeMessage = useResetRecoilState(postIframeMessageState);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    window.addEventListener('message', messageHandler);

    if (onLoadData) iframe.onload = handleOnLoad;

    return () => {
      window.removeEventListener('message', messageHandler);
      if (onLoadData) iframe.onload = null;
    };
  }, [handleOnLoad, messageHandler, onLoadData]);

  useEffect(() => {
    if (iframeRef.current && iframeRef.current.contentWindow && postIframeMessage) {
      iframeRef.current.contentWindow.postMessage(JSON.stringify(postIframeMessage), targetOrigin);
      resetPostIframeMessage();
    }
  }, [postIframeMessage, resetPostIframeMessage, targetOrigin]);

  return <CustomIframe ref={iframeRef} src={pageUrl} {...rest} />;
};

export default Iframe;

const CustomIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;
